import React, { useState, useEffect } from "react";
import Icon from "../../components/em/Icon";

import { API_URL } from '../../api/api';






import { toast } from "../../utils/toast";
import { hasPermission } from "../../utils/permissionUtils";

const formatShortDate = date =>
  new Date(date).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric"
  });

// One queue row's details. A swap request names the colleague and every day;
// an older plain request (the dashboard widget's) names the shift wanted.
const RequestSummary = ({ req, showStatus = false }) => {
  const swapDates = String(req.swap_dates || "").split(",").filter(Boolean);
  return (
    <div>
      <strong>User:</strong> {req.username}
      <br/>
      {req.swap_with_member_id ? (
        <>
          <strong>Swap with:</strong> {req.swap_with_name || `Employee #${req.swap_with_member_id}`}
          <br/>
          <strong>{swapDates.length > 1 ? "Dates:" : "Date:"}</strong>{" "}
          {swapDates.map(formatShortDate).join(", ")}
        </>
      ) : (
        <>
          <strong>Shift:</strong> {req.shift_name}
          {req.shift_date && (<><br/><strong>Date:</strong> {formatShortDate(req.shift_date)}</>)}
        </>
      )}
      {showStatus && (<><br/><strong>Status:</strong> {req.status}</>)}
    </div>
  );
};

const ShiftSwapModal =({ employees, shifts, isReadOnly = false, onSwap, onClose }) => {
  // Whether to offer the queue at all. The cached permission is only a hint:
  // it comes from localStorage written at login, so a grant made mid-session
  // is not there yet, and it says nothing about being someone's line manager.
  // The server answers definitively via /shift-requests/can-review.
  const [canApproveSwaps, setCanApproveSwaps] = useState(
    hasPermission("approve_shift_swaps"),
  );
  const [employee1, setEmployee1] = useState("");
  const [employee2, setEmployee2] = useState("");
  const [assignmentType, setAssignmentType] = useState("single");
  const [dates, setDates] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
const [historyRequests, setHistoryRequests] = useState([]);
  // Raising your own request is opt-in per department (Settings → Departments
  // → "Allow shift swaps"). Unknown (null) until the server answers below.
  const [canRequest, setCanRequest] = useState(null);
  // Creating a swap outright is a roster write (isReadOnly); seeing and
  // deciding the queue is `approve_shift_swaps` or being someone's manager.
  // Raising your own request is open to anyone whose department allows it — a
  // reviewer works shifts too, and had no way to ask for a swap while the queue
  // owned the modal.
  const TABS = [
    ...(canRequest ? ["request"] : []),
    ...(isReadOnly ? [] : ["create"]),
    ...(canApproveSwaps ? ["pending", "history"] : []),
  ];
  const requestOnly = TABS.length === 1 && TABS[0] === "request";
 const [activeTab, setActiveTab] = useState(TABS[0]);
// Tabs arrive after the probe, so an initial pick can be stale (e.g. nothing
// was available yet). Move only when the current tab isn't on offer, so the
// modal never jumps out from under someone mid-interaction.
useEffect(() => {
  if (TABS.length && !TABS.includes(activeTab)) setActiveTab(TABS[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [canRequest, canApproveSwaps, isReadOnly]);
// Request tab — same shape as Create Swap, but Employee 1 is always the
// signed-in employee and the colleague list is limited to swap-enabled
// departments (the server decides both).
const [me, setMe] = useState(null); // { id, name }
const [partners, setPartners] = useState([]);
const [reqPartner, setReqPartner] = useState("");
const [reqType, setReqType] = useState("single");
const [reqDates, setReqDates] = useState([]);
const [submitting, setSubmitting] = useState(false);
// Earliest day a request can be for. Local parts, not toISOString(), which
// rolls back a day for any timezone ahead of UTC.
const todayLocal = (() => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
})();
const shiftbaseURL = `${API_URL}/api/settings/shifts`;

// Create Swap — both dropdowns offer only swap-enabled departments' members,
// the same rule /swap enforces.
const [swapEligible, setSwapEligible] = useState([]);
useEffect(() => {
  if (isReadOnly) return;
  let cancelled = false;
  (async () => {
    try {
      const res = await fetch(`${shiftbaseURL}/swap-eligible`, { headers: authHeaders() });
      if (handle401(res) || !res.ok) return;
      const data = await res.json();
      if (!cancelled) setSwapEligible(Array.isArray(data) ? data : []);
    } catch {
      /* the dropdowns stay empty */
    }
  })();
  return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isReadOnly]);

useEffect(() => {
  if (!canRequest) return;
  let cancelled = false;
  (async () => {
    try {
      const res = await fetch(`${shiftbaseURL}/shift-requests/swap-partners`, {
        headers: authHeaders(),
      });
      if (handle401(res) || !res.ok) return;
      const data = await res.json();
      if (cancelled) return;
      setMe(data?.me || null);
      setPartners(Array.isArray(data?.partners) ? data.partners : []);
    } catch {
      /* the form shows an empty colleague list */
    }
  })();
  return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [canRequest]);
useEffect(() => {
  if (activeTab === "pending") fetchPending();
  if (activeTab === "history") fetchHistory();
}, [activeTab]);

// Ask the server who this is. Catches both a permission granted mid-session
// (localStorage is still stale) and a line manager who holds no permission but
// does have reports whose requests they may decide.
useEffect(() => {
  let cancelled = false;
  (async () => {
    try {
      const res = await fetch(`${shiftbaseURL}/shift-requests/can-review`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        if (!cancelled) setCanRequest(false);
        return;
      }
      const data = await res.json();
      if (cancelled) return;
      setCanRequest(!!data?.canRequest);
      // Only reveals the queue tabs — the active tab is left alone so the
      // modal never jumps out from under someone mid-interaction.
      if (data?.canReview) setCanApproveSwaps(true);
    } catch {
      /* leave the cached permission as the answer */
      if (!cancelled) setCanRequest(false);
    }
  })();
  return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);
  // Single Date REPLACES the selection; Multiple Dates appends to it. This
  // always appended, so picking a second date under "Single Date" left two
  // chips and submitted a two-day swap.
  const addDate = e => {
    const d = e.target.value;
    if (!d) return;
    if (assignmentType === "single") {
      setDates([d]);
      return;
    }
    if (!dates.includes(d)) setDates([...dates, d]);
  };

  const removeDate = d => {
    setDates(dates.filter(x => x !== d));
  };

  // Switching to Single Date keeps the most recent pick rather than silently
  // carrying a list the form no longer shows.
  const changeAssignmentType = e => {
    const next = e.target.value;
    setAssignmentType(next);
    if (next === "single") setDates((prev) => (prev.length ? [prev[prev.length - 1]] : []));
  };
// Request-tab date picking — same single/multiple rules as Create Swap above.
const addReqDate = e => {
  const d = e.target.value;
  if (!d) return;
  if (reqType === "single") {
    setReqDates([d]);
    return;
  }
  if (!reqDates.includes(d)) setReqDates([...reqDates, d]);
};
const changeReqType = e => {
  const next = e.target.value;
  setReqType(next);
  if (next === "single") setReqDates((prev) => (prev.length ? [prev[prev.length - 1]] : []));
};

// The server takes the requester (Employee 1) from the session, so only the
// colleague and the days are sent.
const handleRequestSubmit = async () => {
  if (!reqPartner) {
    toast.error("Choose who you want to swap with");
    return;
  }
  if (!reqDates.length) {
    toast.error("Pick at least one date");
    return;
  }
  setSubmitting(true);
  try {
    await onSwap({ type: "request", swap_with: reqPartner, dates: reqDates });
  } finally {
    setSubmitting(false);
  }
};

// Auth helper for the legacy fetch calls in this modal.
const authHeaders = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const handle401 = (res) => {
  if (res.status === 401) {
    localStorage.clear();
    window.location.assign("/login");
    return true;
  }
  return false;
};

const fetchPending = async () => {
  try {
    const res = await fetch(`${shiftbaseURL}/shift-requests?status=pending`, {
      headers: authHeaders(),
    });
    if (handle401(res)) return;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    setPendingRequests(data);
  } catch (err) {
    console.error("Failed to fetch pending shift requests", err);
    setPendingRequests([]);
  }
};

const fetchHistory = async () => {
  try {
    const res = await fetch(`${shiftbaseURL}/shift-requests?status=history`, {
      headers: authHeaders(),
    });
    if (handle401(res)) return;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    setHistoryRequests(data);
  } catch (err) {
    console.error("Failed to fetch shift request history", err);
    setHistoryRequests([]);
  }
};

const handleApprove = async (id) => {
  try {
    const res = await fetch(`${shiftbaseURL}/shift-requests/${id}/approve`, {
      method: "POST",
      headers: authHeaders(),
    });
    if (handle401(res)) return;
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      toast.error(data.error || `Approve failed (${res.status})`);
      // A swap that can no longer apply goes back to pending; a request
      // someone else already decided leaves the list — refresh either way.
      fetchPending();
      return;
    }
    const data = await res.json().catch(() => ({}));
    toast.success(data.message || "Request approved");
    fetchPending();
  } catch (err) {
    toast.error(err.message || "Approve failed");
  }
};

const handleReject = async (id) => {
  try {
    const res = await fetch(`${shiftbaseURL}/shift-requests/${id}/reject`, {
      method: "POST",
      headers: authHeaders(),
    });
    if (handle401(res)) return;
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      toast.error(data.error || `Reject failed (${res.status})`);
      return;
    }
    fetchPending();
  } catch (err) {
    toast.error(err.message || "Reject failed");
  }
};
  const handleSubmit = () => {
    if (!employee1 || !employee2 || dates.length === 0) {
      toast.error("Fill all required fields");
      return;
    }
    if (employee1 === employee2) {
      toast.error("Employees must be different");
      return;
    }

    onSwap({
      employee1,
      employee2,
      dates,
      type: assignmentType
    });
  };

  const formatDate = d =>
    new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    });

  return (
    <div className="modal-overlay">
      <div className="swap-modal modal">

        {/* Header */}


        <div className="title-btns">
          <div className="icn-subtit">
            <span className="icon-wrap"><Icon name="swap" /></span>
            <h3>Shift Swaps</h3>
            <p>{canApproveSwaps || !isReadOnly ? "Manage shift swap requests" : "Request a shift swap"}</p>
          </div>
          <div><div className="close-btn" onClick={onClose}><Icon name="close"/></div></div>
        </div>






        {/* Tabs — hidden when the request form is the only thing on offer,
            since a lone tab is just a label that does nothing. */}
     {!requestOnly && (
     <ul className="mode-tabs">
  {TABS.map(tab => (

    <li
      key={tab}
      className={activeTab === tab ? "active" : ""}
      onClick={()=>setActiveTab(tab)}
    >
      {tab === "create" && "Create Swap"}
      {tab === "pending" && "Pending"}
      {tab === "history" && "History"}
      {tab === "request" && "Request Swap"}
    </li>
  ))}
</ul>
     )}

{/* Nothing on offer: a member whose department hasn't opted in and who
    reviews no one's requests. Blank while the probe is still out. */}
{TABS.length === 0 && (
  <div className="form-wrap">
    <div className="form-item">
      <p>
        {canRequest === null
          ? "Loading…"
          : "Shift swap requests aren't enabled for your department."}
      </p>
    </div>
  </div>
)}

{TABS.includes(activeTab) && activeTab === "create" && (

<div className="form-wrap">
    {/* Employees */}
    <div className="form-item">
      <p>Create a shift swap between two employees. This will automatically approve the swap.</p>
    </div>

      <div className="col-2">
      <div className="form-item">
        <label>Employee 1 *</label>
        <select value={employee1} onChange={e=>setEmployee1(e.target.value)}>
          <option value="">Select employee</option>
          {swapEligible.map(e=>(
            <option key={e.id} value={e.id}>
              {e.first_name} {e.last_name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-item">
        <label>Employee 2 *</label>
        <select value={employee2} onChange={e=>setEmployee2(e.target.value)}>
          <option value="">Select employee</option>
          {swapEligible.map(e=>(
            <option key={e.id} value={e.id}>
              {e.first_name} {e.last_name}
            </option>
          ))}
        </select>
      </div>
    </div>
    

    {/* Assignment Type */}
    <div className="form-item">
      <label>Assignment Type *</label>
      <select value={assignmentType} onChange={changeAssignmentType}>
        <option value="single">Single Date</option>
        <option value="multiple">Multiple Dates</option>
      </select>
    </div>

    {/* Date */}
    <div className="form-item">
      <label>{assignmentType === "single" ? "Select Date *" : "Add Dates *"}</label>
      {/* Controlled in single mode so the field shows the one chosen date; in
          multiple mode it clears after each pick, ready for the next. */}
      <input
        type="date"
        value={assignmentType === "single" ? (dates[0] || "") : ""}
        onChange={addDate}
      />
      {assignmentType === "multiple" && (
        <small>Pick a date to add it to the list below.</small>
      )}
    </div>

    {/* Chips — only for multiple dates. In single mode the input above already
        shows the selection, so a chip would just repeat it. */}
    {assignmentType === "multiple" && (
      <div className="date-list">
        {dates.map(d=>(
          <span key={d} className="chip">
            {formatDate(d)}
            <button onClick={()=>removeDate(d)}>×</button>
          </span>
        ))}
      </div>
    )}
<div className="form-action">
    <button className="orng-fill" onClick={handleSubmit}>
      Create & Auto-Approve Swap
    </button>
  </div>
  </div>

)}
{TABS.includes(activeTab) && activeTab==="request" && (


<div className="form-wrap">
  <div className="form-item">
    <p>Swap your shift with a colleague. It goes to your manager, and the shifts swap once it's approved.</p>
  </div>

  <div className="col-2">
    {/* Always you — the server takes the requester from the session. */}
    <div className="form-item">
      <label>Employee 1 *</label>
      <select value="me" disabled>
        <option value="me">{me?.name || "You"}</option>
      </select>
    </div>

    <div className="form-item">
      <label>Swap with *</label>
      <select value={reqPartner} onChange={e=>setReqPartner(e.target.value)}>
        <option value="">Select employee</option>
        {partners.map(p=>(
          <option key={p.id} value={p.id}>
            {p.first_name} {p.last_name}
          </option>
        ))}
      </select>
    </div>
  </div>

  <div className="form-item">
    <label>Assignment Type *</label>
    <select value={reqType} onChange={changeReqType}>
      <option value="single">Single Date</option>
      <option value="multiple">Multiple Dates</option>
    </select>
  </div>

  <div className="form-item">
    <label>{reqType === "single" ? "Select Date *" : "Add Dates *"}</label>
    <input
      type="date"
      min={todayLocal}
      value={reqType === "single" ? (reqDates[0] || "") : ""}
      onChange={addReqDate}
    />
    {reqType === "multiple" && (
      <small>Pick a date to add it to the list below.</small>
    )}
  </div>

  {reqType === "multiple" && (
    <div className="date-list">
      {reqDates.map(d=>(
        <span key={d} className="chip">
          {formatDate(d)}
          <button onClick={()=>setReqDates(reqDates.filter(x => x !== d))}>×</button>
        </span>
      ))}
    </div>
  )}

  <div className="form-action">
    <button className="orng-fill" onClick={handleRequestSubmit} disabled={submitting}>
      {submitting ? "Submitting…" : "Submit Swap Request"}
    </button>
  </div>
</div>
)}


{activeTab==="pending" && (
  <div className="list pending-wrap">
  {!pendingRequests.length && <p>No pending requests</p>}

  {pendingRequests.map(req=>(
    <div key={req.id} className="req-row">

      <RequestSummary req={req} />

      <div className="actions">
        <button className="org-gradi" onClick={()=>handleApprove(req.id)}>Approve</button>
        <button onClick={()=>handleReject(req.id)}>Reject</button>
      </div>

    </div>
  ))}
</div>
)}

{activeTab==="history" && (
  <div className="list history-wrap">

  {!historyRequests.length && <p>No history</p>}

  {historyRequests.map(req=>(
    <div key={req.id} className="req-row">

      <RequestSummary req={req} showStatus />


      <small>
        Reviewed at {formatShortDate(req.reviewed_at)}
      </small>
       <small>
        Reviewed By {req.reviewed_by_name}
      </small>
    </div>
  ))}
</div>
)}
      


      </div>
    </div>
  );
};

export default ShiftSwapModal;